<template>
  <div class="module-form">
    <validation-observer ref="simpleRules">
      <b-form @submit.prevent="handleSubmit">
        <b-row>
          <b-col md="6">
            <b-form-group label="Module Name" label-for="name">
              <label style="color: red !important">*</label>
              <validation-provider
                #default="{ errors }"
                rules="required"
                name="Module Name"
              >
                <b-form-input
                  id="name"
                  v-model="formData.name"
                  placeholder="Enter Module Name"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Software ID" label-for="sid">
              <label style="color: red !important">*</label>
              <validation-provider
                #default="{ errors }"
                rules="required|numeric"
                name="Software ID"
              >
                <b-form-input
                  id="sid"
                  v-model.number="formData.sid"
                  type="number"
                  placeholder="Enter Software ID"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Parent" label-for="parent">
              <b-form-input
                id="parent"
                v-model="formData.parent"
                placeholder="Enter Parent (optional)"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Icon" label-for="icon">
              <b-form-input
                id="icon"
                v-model="formData.icon"
                placeholder="Enter Icon Name (optional)"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="URL" label-for="url">
              <b-form-input
                id="url"
                v-model="formData.url"
                placeholder="Enter Module URL (optional)"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Sort Order" label-for="sort">
              <b-form-input
                id="sort"
                v-model.number="formData.sort"
                type="number"
                placeholder="Enter Sort Order"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group label="Routes">
              <div
                v-for="(route, index) in formData.routes"
                :key="index"
                class="route-item mb-2"
              >
                <b-input-group>
                  <b-form-input
                    v-model="route.route"
                    :placeholder="`Route ${index + 1}`"
                  />
                  <b-input-group-append>
                    <b-button
                      variant="danger"
                      size="sm"
                      @click="removeRoute(index)"
                      :disabled="formData.routes.length === 1"
                    >
                      <feather-icon icon="XIcon" size="14" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>
              <b-button
                variant="secondary"
                size="sm"
                @click="addRoute"
                class="mt-2"
              >
                <feather-icon icon="PlusIcon" class="mr-50" size="14" />
                Add Route
              </b-button>
            </b-form-group>
          </b-col>
        </b-row>

        <b-alert
          v-if="error"
          show
          variant="danger"
          dismissible
          @dismissed="error = ''"
          class="mt-2"
        >
          {{ error }}
        </b-alert>

        <b-alert
          v-if="success"
          show
          variant="success"
          dismissible
          @dismissed="success = ''"
          class="mt-2"
        >
          {{ success }}
        </b-alert>

        <div v-if="!isModal" class="mt-3">
          <b-button
            type="submit"
            variant="primary"
            :disabled="loading"
            class="mr-1"
          >
            {{ isEditMode ? 'Update' : 'Create' }} Module
          </b-button>
          <b-button
            variant="secondary"
            @click="$router.go(-1)"
            :disabled="loading"
          >
            Cancel
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { moduleService } from '@/services/moduleService';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BCard,
  BCardHeader,
  BCardBody,
  BCardTitle,
  BInputGroup,
  BInputGroupAppend,
  BAlert,
} from 'bootstrap-vue';
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue';

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BAlert,
    FeatherIcon,
  },
  props: {
    isModal: {
      type: Boolean,
      default: false,
    },
    moduleId: {
      type: [Number, String],
      default: null,
    },
    closePopUp: {
      type: Function,
      default: null,
    },
    getGridData: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      isEditMode: false,
      loading: false,
      error: '',
      success: '',
      formData: {
        name: '',
        sid: null,
        parent: null,
        icon: '',
        url: '',
        installed: 1,
        sort: 1,
        routes: [{ route: '' }],
      },
    };
  },
  watch: {
    moduleId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.isEditMode = true;
          this.loadModule(newVal);
        } else {
          this.resetForm();
        }
      },
    },
  },
  mounted() {
    // If not in modal mode, check route params
    if (!this.isModal && this.$route.params.id) {
      this.isEditMode = true;
      this.loadModule(this.$route.params.id);
    } else if (this.isModal && this.moduleId) {
      this.isEditMode = true;
      this.loadModule(this.moduleId);
    } else if (this.isModal) {
      this.resetForm();
    }
  },
  methods: {
    // Reset form to initial state
    resetForm() {
      this.isEditMode = false;
      this.error = '';
      this.success = '';
      this.formData = {
        name: '',
        sid: null,
        parent: null,
        icon: '',
        url: '',
        installed: 1,
        sort: 1,
        routes: [{ route: '' }],
      };
      if (this.$refs.simpleRules) {
        this.$refs.simpleRules.reset();
      }
    },

    // Add new route field
    addRoute() {
      this.formData.routes.push({ route: '' });
    },

    // Remove route field
    removeRoute(index) {
      if (this.formData.routes.length > 1) {
        this.formData.routes.splice(index, 1);
      }
    },

    // Load module data for editing
    async loadModule(id) {
      try {
        this.loading = true;
        this.error = '';
        const response = await moduleService.getModuleById(id);
        if (response.data && response.data.length > 0) {
          const module = response.data[0];
          this.formData.name = module.name || '';
          this.formData.sid = module.sid || null;
          this.formData.parent = module.parent || null;
          this.formData.icon = module.icon || '';
          this.formData.url = module.url || '';
          this.formData.installed = module.installed || 1;
          this.formData.sort = module.sort || 1;

          // Load routes
          if (module.routes && module.routes.length > 0) {
            this.formData.routes = module.routes.map((r) => ({
              route: r.route || '',
            }));
          } else {
            this.formData.routes = [{ route: '' }];
          }
        }
      } catch (err) {
        this.error = err.message || 'Failed to load module';
      } finally {
        this.loading = false;
      }
    },

    // Handle form submission
    async handleSubmit() {
      try {
        const isValid = await this.$refs.simpleRules.validate();
        if (!isValid) {
          return false;
        }

        this.loading = true;
        this.error = '';
        this.success = '';

        // Filter out empty routes
        const routes = this.formData.routes
          .filter((r) => r.route && r.route.trim() !== '')
          .map((r) => ({ route: r.route.trim() }));

        const payload = {
          ...this.formData,
          routes: routes,
        };

        let response;
        if (this.isEditMode) {
          const moduleId = this.isModal ? this.moduleId : this.$route.params.id;
          response = await moduleService.updateModule(moduleId, payload);
          this.success = 'Module updated successfully!';
        } else {
          response = await moduleService.createModule(payload);
          this.success = 'Module created successfully!';
        }

        // If in modal mode, refresh grid and close modal
        if (this.isModal) {
          if (this.getGridData) {
            this.getGridData();
          }
          setTimeout(() => {
            if (this.closePopUp) {
              this.closePopUp();
            }
            this.resetForm();
          }, 1500);
        } else {
          // Redirect to edit page or list
          if (response.data && response.data.id && !this.isEditMode) {
            setTimeout(() => {
              this.$router.push(`/modules/${response.data.id}/edit`);
            }, 1500);
          }
        }
        return true;
      } catch (err) {
        this.error = err.message || 'An error occurred';
        if (err.errors) {
          const errorMessages = Object.values(err.errors).flat();
          this.error = errorMessages.join(', ');
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.module-form {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.module-form:not(.modal-form) {
  padding: 20px;
}

.route-item {
  display: flex;
  gap: 10px;
}

.route-item .form-control {
  flex: 1;
}
</style>

